/*
 * SPDX-License-Identifier: MIT
 * Copyright (c) 2026 Henry Fan
 *
 * Adapted from the public Background Lab renderer by Henry Fan under the MIT
 * License. This Hub-specific edition retains the public fBm/wind glyph-field
 * approach and adds the interaction/API behavior below; no Anthropic source,
 * assets, fonts, or recovered implementation is included.
 */
// @ts-nocheck
/*
 * SPDX-License-Identifier: MIT
 * Copyright (c) 2026 Henry Fan
 *
 * Local ESM adaptation of the licensed GlyphWind renderer. The full MIT text is
 * retained at third-party/glyph-flow-refined/LICENSE-Henry-Fan-MIT.txt.
 */
'use strict';

  const clamp = (value, low = 0, high = 1) => Math.max(low, Math.min(high, value));
  const smooth = value => { const n = clamp(value); return n * n * (3 - 2 * n); };
  const charsets = {
    mixed: 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
    lettered: 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ',
    figured: '0123456789ABCDEFabcdef'
  };

  function defaults() {
    return {
      seed: undefined,
      look: 'mixed',
      strength: { scale: 1, base: { dark: 0.26 }, cap: { dark: 0.9 }, rest: 0 },
      surface: { fill: 1, weight: 400, glyphCss: 9 },
      wind: {
        energy: 0.54, friction: 0.62, flick: 1.12, lull: 0.48, streak: 0.64,
        detail: 0.66, edge: 0.76, curl: 0.72, wisp: 0.62, tail: 0.38,
        sweep: 0.28, morph: 0.45, revealS: 1
      },
      glow: { amount: 0.24, color: { dark: [65 / 255, 214 / 255, 238 / 255] } },
      touch: { stir: 1, ripple: 1, slowPxS: 60, fastPxS: 800 },
      composition: { card: { centerY: 0.49, calmX: 280, calmY: 190, left: 0.10 } },
      refinement: {
        visualStyle: 'refined',
        depth: { minScale: 0.86, maxScale: 1, minAlpha: 0.55, maxAlpha: 1.03 }
      },
      grid: {
        cellCss: 7.8, mobileCellCss: 9, maxCells: 20000, mobileMaxCells: 4000,
        minCols: 24, minRows: 8, dprCap: 1.5, maxDevicePixels: 4000000
      }
    };
  }

  function random(seed) {
    let state = (seed >>> 0) || 0x19a4c637;
    return () => {
      state ^= state << 13; state ^= state >>> 17; state ^= state << 5;
      return (state >>> 0) / 4294967296;
    };
  }

  function pattern(seed, look = 'mixed') {
    const rng = random(seed), between = (low, high) => low + (high - low) * rng();
    const angle = between(Math.PI / 10, Math.PI / 4.3);
    return {
      seed: seed >>> 0,
      key: look,
      heading: rng() < 0.5 ? angle : Math.PI - angle,
      speed: between(42, 64),
      gustLength: between(380, 560),
      gustWidth: between(120, 190),
      bend: between(60, 110),
      bendScale: between(300, 440)
    };
  }

  function grid(bounds, options) {
    const cssWidth = Math.max(0, Number(bounds.cssWidth ?? bounds.width ?? 0));
    const cssHeight = Math.max(0, Number(bounds.cssHeight ?? bounds.height ?? 0));
    let scale = Math.min(Math.max(0.1, Number(bounds.devicePixelRatio ?? 1)), options.dprCap);
    if (!cssWidth || !cssHeight) return null;
    const requestedPixels = cssWidth * cssHeight * scale * scale;
    if (requestedPixels > options.maxDevicePixels) {
      scale *= Math.sqrt(options.maxDevicePixels / requestedPixels);
    }
    const mobile = bounds.mobile ?? cssWidth <= 767;
    const cellCss = mobile ? options.mobileCellCss : options.cellCss;
    const cellWidth = Math.max(5, Math.round(cellCss * scale));
    const cellHeight = Math.max(10, Math.round(cellCss * 2 * scale));
    const deviceWidth = Math.max(1, Math.round(cssWidth * scale));
    const deviceHeight = Math.max(1, Math.round(cssHeight * scale));
    let columns = Math.ceil(deviceWidth / cellWidth);
    let rows = Math.ceil(deviceHeight / cellHeight);
    if (columns < options.minCols || rows < options.minRows) return null;
    const maxCells = mobile ? options.mobileMaxCells : options.maxCells;
    const densityScale = Math.max(1, Math.sqrt((columns * rows) / maxCells));
    columns = Math.max(options.minCols, Math.floor(columns / densityScale));
    rows = Math.max(options.minRows, Math.floor(rows / densityScale));
    return {
      columns, rows, cellWidth, cellHeight, scale, mobile,
      cssWidth, cssHeight,
      left: Math.round((deviceWidth - columns * cellWidth) / 2),
      top: Math.round((deviceHeight - rows * cellHeight) / 2)
    };
  }

  function chooseLook(seed, history = []) {
    const keys = Object.keys(charsets);
    const available = keys.filter(key => !history.slice(0, 2).includes(key));
    const pool = available.length ? available : keys;
    return pool[(seed >>> 0) % pool.length];
  }

  // Public-source fBm cloud and wind structure, with Hub-specific local fields
  // for irregular physical tap impulses rather than decorative fixed circles.
  const vertex = `#version 300 es
precision highp float;
precision highp int;
uniform vec2 viewport, cellSize, gridOrigin, pane;
uniform int columns, sequence[64], sequenceLength;
uniform float seed, clockTime, wallTime, reveal, fillRate, changeRate;
uniform vec4 flow, bending, textureShape, activity;
uniform vec4 quiet, depthStyle;
uniform float tail, sweep, evolve;
uniform int tapCount, wakeCount;
uniform vec4 taps[6], tapShapes[6], wakes[48];
out vec2 atlasUV;
out float luminance, blue, depth;
const vec2 corners[6] = vec2[6](vec2(0,0),vec2(1,0),vec2(1,1),vec2(0,0),vec2(1,1),vec2(0,1));
float hash(vec2 p) { return fract(sin(dot(p,vec2(127.13,311.77)) + seed*.017) * 43758.31); }
float noise(vec2 p) {
  vec2 base=floor(p), q=fract(p); q=q*q*(3.-2.*q);
  return mix(mix(hash(base),hash(base+vec2(1,0)),q.x),mix(hash(base+vec2(0,1)),hash(base+vec2(1,1)),q.x),q.y);
}
float cloud(vec2 p, float age) {
  float sum=0., weight=.5, total=0.;
  for(int k=0;k<4;k++) {
    vec2 drift=age*vec2(.019+float(k)*.007,-.013-float(k)*.004);
    sum+=weight*noise(p+drift+float(k)*13.37); total+=weight;
    p=mat2(1.74,-.58,.58,1.74)*p+vec2(7.9,21.2);
    weight*=mix(.35,.65,textureShape.x);
  }
  return sum/total;
}
float pulseLobes(float phase) {
  return 2. + floor(fract(sin(phase*17.71)*43758.5453)*4.);
}
void main() {
  int row=gl_InstanceID/columns, col=gl_InstanceID-row*columns;
  vec2 cell=vec2(col,row);
  vec2 pixel=gridOrigin+(cell+.5)*cellSize;
  vec2 p=pixel*pane/viewport;
  if(hash(cell+19.)>fillRate) {
    gl_Position=vec4(2,2,0,1); atlasUV=vec2(0); luminance=0.; blue=0.; depth=0.; return;
  }
  float age=clockTime*evolve;
  float tapField=0., tapCycle=0., tapGlow=0.;
  vec2 localWarp=vec2(0.);
  for(int j=0;j<6;j++) {
    if(j>=tapCount) break;
    float t=max(0.,wallTime-taps[j].z);
    vec4 shape=tapShapes[j];
    float strength=abs(taps[j].w);
    float speed=360. + 230.*fract(sin(shape.x*3.17+shape.w*5.31)*43758.5453);
    float radius=speed*(1.-exp(-t/1.25));
    float width=72.+64.*t;
    vec2 offset=p-taps[j].xy;
    float c=cos(shape.x), s=sin(shape.x);
    vec2 rotated=mat2(c,s,-s,c)*offset;
    rotated.x/=max(.58,shape.y);
    float radial=length(rotated);
    float theta=atan(rotated.y,rotated.x);
    float lobe=pulseLobes(shape.w);
    float contour=1. + shape.z*sin(lobe*theta+shape.w+t*.82) + shape.z*.42*sin((lobe+1.)*theta-shape.w*1.7);
    float distance=abs(radial-radius*contour);
    float ring=exp(-4.*distance*distance/(width*width));
    float body=exp(-radial*radial/max(1.,(radius+width*1.8)*(radius+width*1.8)));
    float envelope=(1.-exp(-t/.105))*exp(-t/1.55)*(1.-smoothstep(2.6,3.8,t))*strength;
    float push=envelope*(ring*.96+body*.15);
    vec2 radialDirection=offset/max(1.,length(offset));
    localWarp+=radialDirection*push*(18.+20.*shape.z)*sin(theta*lobe+shape.w);
    tapField+=push;
    tapCycle+=push*(4.+lobe);
    tapGlow+=ring*envelope;
  }
  float warmth=0.;
  for(int j=0;j<48;j++) {
    if(j>=wakeCount) break;
    float t=max(0.,wallTime-wakes[j].z), radius=50.+40.*t;
    float heat=(1.-exp(-t/.05))*exp(-t/.65);
    warmth+=wakes[j].w*heat*exp(-dot(p-wakes[j].xy,p-wakes[j].xy)/(radius*radius));
  }
  warmth=1.-exp(-1.5*warmth);
  vec2 dir=vec2(cos(flow.x),sin(flow.x));
  vec2 across=vec2(-dir.y,dir.x);
  vec2 bend=vec2(noise(p/bending.y+age*.012),noise(p/bending.y+vec2(21.3,-age*.009)))-.5;
  vec2 shifted=p+bend*bending.x*2. + localWarp;
  vec2 q=vec2(dot(shifted,dir)-clockTime*flow.y,dot(shifted,across));
  vec2 eddy=vec2(noise(q/(flow.w*1.2)+age*.014),noise(q/(flow.w*.8)+vec2(7.2,-age*.01)))-.5;
  q+=eddy*flow.w*textureShape.z*1.2;
  float field=cloud(q/flow.zw,age);
  float threads=1.-smoothstep(0.,.28,abs(noise(q/vec2(140.,30.)+age*.008)-.5));
  field+=textureShape.w*.1*(threads-.5)+sweep*.08*cos(q.x/max(pane.x*.17,120.));
  // Interaction changes the visibility field and glyph cadence, not just color.
  field+=tapField*.34+warmth*.28;
  float threshold=.28+bending.z*.4-tail*.035;
  float band=mix(.19,.045,textureShape.y);
  float lit=pow(smoothstep(threshold-band,threshold+band,field),2.);
  lit*=mix(1.,.32+.68*threads,bending.w);
  lit=mix(lit,max(lit,.68*cloud(q/vec2(35.,22.),age)),warmth);
  vec2 center=(p-vec2(pane.x*.5,pane.y*quiet.w))/quiet.xy;
  float calm=1.-(1.-quiet.z)*exp(-.9*dot(center,center));
  float shown=max(reveal,clamp(warmth+tapField,0.,1.));
  luminance=(min(1.,lit+tapField*.55)+activity.x)*calm*shown;
  blue=tapGlow*activity.y;
  int stepIndex=int(floor(clockTime*changeRate*(.6+.8*hash(cell+9.))+hash(cell)*50.+tapCycle));
  int glyph=sequence[(int(hash(cell+77.)*997.)+stepIndex)%sequenceLength];
  depth=hash(cell+vec2(53.17,91.29));
  float glyphScale=mix(depthStyle.x,depthStyle.y,depth);
  vec2 corner=corners[gl_VertexID];
  atlasUV=(vec2(glyph%16,glyph/16)+corner)/vec2(16.,6.);
  vec2 location=gridOrigin+(cell+.5+(corner-.5)*glyphScale)*cellSize;
  gl_Position=vec4(location/viewport*vec2(2.,-2.)+vec2(-1.,1.),0,1);
}`;

  const fragment = `#version 300 es
precision highp float;
uniform sampler2D atlas;
uniform vec3 inkColor, glowColor;
uniform float inkOpacity, friction;
uniform vec4 depthStyle;
in vec2 atlasUV;
in float luminance, blue, depth;
out vec4 color;
void main() {
  float coverage=texture(atlas,atlasUV).a;
  if(coverage<.001) discard;
  float shading=mix(1.,.7+.3*coverage,friction);
  float depthAlpha=mix(depthStyle.z,depthStyle.w,depth);
  float alpha=coverage*clamp((luminance*inkOpacity*shading/3.+blue*.58)*depthAlpha,0.,1.);
  if(alpha<.002) discard;
  float lighting=smoothstep(.32,.94,luminance+blue*.48);
  float front=smoothstep(.68,1.,depth);
  vec3 base=mix(inkColor,glowColor,front*(.08+.12*lighting));
  vec3 rgb=mix(base,glowColor,smoothstep(.04,.28,blue)*(.72+.28*lighting));
  color=vec4(rgb*alpha,alpha);
}`;

  function compile(gl, type, source) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const message = gl.getShaderInfoLog(shader) || 'WebGL shader compilation failed.';
      gl.deleteShader(shader);
      throw new Error(message);
    }
    return shader;
  }

  function cryptoSeed(value) {
    if (Number.isFinite(value)) return Number(value) >>> 0;
    const bytes = new Uint32Array(1);
    if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(bytes);
    else bytes[0] = Math.floor(Math.random() * 4294967296);
    return bytes[0] || 0x19a4c637;
  }

  function create({ element, settings = defaults, timing = () => ({ fadeIn: 700 }), onReady, onError, onContextLost } = {}) {
    if (!(element instanceof Element)) throw new TypeError('GlyphWind.create requires a host element.');
    const canvas = document.createElement('canvas');
    canvas.id = 'code-ripple-canvas';
    canvas.className = 'code-ripple-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;pointer-events:none;z-index:0;opacity:0;';
    element.append(canvas);

    let gl, program, atlasTexture, resizeObserver, view = null, destroyed = false, started = false, contextLost = false;
    let frame = 0, lastFrame = 0, lastCharset = '', lastInk = '';
    const uniforms = new Map();
    const initial = settings();
    initial.seed = cryptoSeed(initial.seed);
    initial.look = initial.look || chooseLook(initial.seed);
    const patternField = initial.field || pattern(initial.seed, initial.look);
    const patternOffset = (initial.seed >>> 0) / 4294967296 * 64;
    const state = {
      timeMs: 0, requestedRunning: true, running: false, reducedMotion: false,
      documentVisible: !document.hidden, manual: false, renderCount: 0,
      introStartMs: 0, taps: [], wakes: [], lastImpulse: null,
      pointer: null, lastWake: null, velocity: 0
    };
    const inkParser = document.createElement('canvas').getContext('2d', { willReadFrequently: true });

    const location = name => {
      if (!uniforms.has(name)) uniforms.set(name, gl.getUniformLocation(program, name));
      return uniforms.get(name);
    };
    const one = (name, value) => gl.uniform1f(location(name), value);
    const integer = (name, value) => gl.uniform1i(location(name), value);
    const two = (name, x, y) => gl.uniform2f(location(name), x, y);
    const four = (name, ...values) => gl.uniform4f(location(name), ...values);
    const currentMs = () => state.timeMs;
    const currentSeconds = () => state.timeMs / 1000;

    function updateInk() {
      const css = getComputedStyle(element).color || '#65b9cc';
      if (css === lastInk) return;
      lastInk = css;
      if (!inkParser) {
        gl.uniform3f(location('inkColor'), 0.396, 0.725, 0.8);
        one('inkOpacity', 1);
        return;
      }
      inkParser.clearRect(0, 0, 1, 1);
      inkParser.fillStyle = css;
      inkParser.fillRect(0, 0, 1, 1);
      const bytes = inkParser.getImageData(0, 0, 1, 1).data;
      gl.uniform3f(location('inkColor'), bytes[0] / 255, bytes[1] / 255, bytes[2] / 255);
      one('inkOpacity', bytes[3] / 255);
    }

    function setOpacity(instant = false) {
      if (!canvas.isConnected) return;
      const fade = Math.max(0, Number(timing().fadeIn ?? 700));
      canvas.style.transition = `opacity ${instant ? 0 : fade}ms ease-out`;
      canvas.style.opacity = state.documentVisible ? '0.75' : '0';
      canvas.dataset.state = state.manual ? 'snapshot' : state.running ? 'moving' : 'still';
    }

    function makeAtlas() {
      if (!view || !gl) return;
      const atlas = document.createElement('canvas');
      atlas.width = view.cellWidth * 16;
      atlas.height = view.cellHeight * 6;
      const context = atlas.getContext('2d');
      const config = settings();
      const family = getComputedStyle(element).fontFamily || 'Consolas, monospace';
      const fontSize = Math.max(7, Math.round((view.mobile ? 8 : config.surface.glyphCss) * view.scale));
      context.font = `${config.surface.weight} ${fontSize}px ${family}`;
      context.fillStyle = '#fff';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      for (let slot = 0; slot < 96; slot += 1) {
        context.fillText(slot === 95 ? '·' : String.fromCharCode(slot + 32), (slot % 16 + 0.5) * view.cellWidth, (Math.floor(slot / 16) + 0.5) * view.cellHeight);
      }
      gl.bindTexture(gl.TEXTURE_2D, atlasTexture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, atlas);
    }

    function resize() {
      if (destroyed || !started) return;
      const box = element.getBoundingClientRect();
      view = grid({ cssWidth: box.width, cssHeight: box.height, devicePixelRatio: window.devicePixelRatio || 1 }, settings().grid);
      canvas.style.visibility = view ? 'visible' : 'hidden';
      if (!view) return;
      const width = Math.max(1, Math.round(view.cssWidth * view.scale));
      const height = Math.max(1, Math.round(view.cssHeight * view.scale));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        makeAtlas();
      }
      draw(true);
    }

    function trimFields() {
      const now = currentSeconds();
      state.taps = state.taps.filter(tap => now - tap.at < 3.8);
      state.wakes = state.wakes.filter(wake => now - wake.at < 2.4);
    }

    function draw(instantReveal = false) {
      if (!started || !view || destroyed || contextLost) return;
      trimFields();
      const config = settings();
      const card = config.composition.card;
      const refinement = config.refinement;
      const calmX = view.mobile ? Math.min(card.calmX, view.cssWidth * .28) : card.calmX;
      const time = currentSeconds();
      const reveal = instantReveal || state.manual || state.reducedMotion
        ? 1
        : smooth((state.timeMs - state.introStartMs) / Math.max(1, config.wind.revealS * 1000));
      gl.useProgram(program);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      two('viewport', canvas.width, canvas.height);
      two('pane', canvas.width / view.scale, canvas.height / view.scale);
      two('cellSize', view.cellWidth, view.cellHeight);
      two('gridOrigin', view.left, view.top);
      integer('columns', view.columns);
      one('seed', (initial.seed >>> 0) % 65521);
      one('clockTime', patternOffset + time);
      one('wallTime', time);
      one('reveal', reveal);
      one('fillRate', config.surface.fill);
      one('changeRate', config.wind.flick);
      four('flow', patternField.heading, patternField.speed, patternField.gustLength, patternField.gustWidth);
      four('bending', patternField.bend, patternField.bendScale, config.wind.lull, config.wind.streak);
      four('textureShape', config.wind.detail, config.wind.edge, config.wind.curl, config.wind.wisp);
      four('quiet', calmX, card.calmY, card.left, card.centerY);
      four('depthStyle', refinement.depth.minScale, refinement.depth.maxScale, refinement.depth.minAlpha, refinement.depth.maxAlpha);
      four('activity', config.strength.rest, config.glow.amount, 0, 0);
      one('tail', config.wind.tail);
      one('sweep', config.wind.sweep);
      one('evolve', config.wind.morph);
      one('friction', config.wind.friction);
      integer('tapCount', state.taps.length);
      integer('wakeCount', state.wakes.length);
      if (state.taps.length) {
        const taps = new Float32Array(state.taps.length * 4);
        const shapes = new Float32Array(state.taps.length * 4);
        state.taps.forEach((tap, index) => {
          taps.set([tap.x, tap.y, tap.at, tap.strength], index * 4);
          shapes.set(tap.shape, index * 4);
        });
        gl.uniform4fv(location('taps'), taps);
        gl.uniform4fv(location('tapShapes'), shapes);
      }
      if (state.wakes.length) {
        const wakes = new Float32Array(state.wakes.length * 4);
        state.wakes.forEach((wake, index) => wakes.set([wake.x, wake.y, wake.at, wake.strength], index * 4));
        gl.uniform4fv(location('wakes'), wakes);
      }
      const charset = charsets[initial.look] || charsets.mixed;
      if (charset !== lastCharset) {
        lastCharset = charset;
        const sequence = new Int32Array(64);
        [...charset].forEach((character, index) => { sequence[index] = character.charCodeAt(0) - 32; });
        gl.uniform1iv(location('sequence'), sequence);
        integer('sequenceLength', charset.length);
      }
      updateInk();
      gl.uniform3fv(location('glowColor'), config.glow.color.dark);
      gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, view.columns * view.rows);
      state.renderCount += 1;
    }

    function stopFrame() {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      state.running = false;
      lastFrame = 0;
    }

    function canAnimate() {
      return started && !destroyed && !contextLost && state.requestedRunning && !state.manual && !state.reducedMotion && state.documentVisible && !document.hidden;
    }

    function tick(now) {
      frame = 0;
      if (!canAnimate()) { state.running = false; return; }
      if (!lastFrame) lastFrame = now;
      const delta = now - lastFrame;
      if (delta >= 33) {
        state.timeMs += Math.min(delta, 250);
        lastFrame = now;
        draw(false);
      }
      state.running = true;
      frame = requestAnimationFrame(tick);
    }

    function schedule() {
      if (!canAnimate() || frame) return;
      state.running = true;
      lastFrame = performance.now();
      frame = requestAnimationFrame(tick);
    }

    function makeShape(index, override) {
      const stream = random((initial.seed + 0x9e3779b9 * (index + 1)) >>> 0);
      const requested = Array.isArray(override) ? override : null;
      const angle = Number.isFinite(requested?.[0]) ? requested[0] : stream() * Math.PI * 2;
      const aspect = Number.isFinite(requested?.[1]) ? requested[1] : 0.66 + stream() * 0.72;
      const wobble = Number.isFinite(requested?.[2]) ? requested[2] : 0.08 + stream() * 0.12;
      const phase = Number.isFinite(requested?.[3]) ? requested[3] : stream() * Math.PI * 2;
      return [angle, clamp(aspect, 0.55, 1.45), clamp(wobble, 0.08, 0.2), phase];
    }

    function diagnosticTap(tap) {
      if (!tap) return null;
      const [angle, aspectRatio, wobble, phase] = tap.shape;
      const growthNoise = Math.sin(angle * 3.17 + phase * 5.31) * 43758.5453;
      const growthPxS = 360 + 230 * (growthNoise - Math.floor(growthNoise));
      return {
        x: tap.x, y: tap.y, atMs: Math.round(tap.at * 1000), strength: tap.strength,
        shape: { angle, aspectRatio, wobble, phase, growthPxS },
        shapeParams: [angle, aspectRatio, wobble, phase]
      };
    }

    function impulse(x, y, atMs, shape) {
      if (!started || !state.requestedRunning || state.reducedMotion) return false;
      if (!Number.isFinite(x) || !Number.isFinite(y) || !view) return false;
      const at = Number.isFinite(atMs) ? Math.max(0, atMs) / 1000 : currentSeconds();
      const index = (state.lastImpulse?.index ?? -1) + 1;
      const variation = random((initial.seed ^ ((index + 1) * 0x85ebca6b)) >>> 0)();
      const tap = {
        x: clamp(Number(x), 0, view.cssWidth),
        y: clamp(Number(y), 0, view.cssHeight),
        at,
        strength: 0.72 + variation * 0.68,
        shape: makeShape(index, shape),
        index
      };
      state.taps.push(tap);
      state.taps = state.taps.slice(-6);
      state.lastImpulse = tap;
      draw(state.manual || state.reducedMotion);
      schedule();
      return true;
    }

    function wakeInput(x, y, atMs) {
      if (!started || !state.requestedRunning || state.reducedMotion || !view) return false;
      if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
      const at = Number.isFinite(atMs) ? Math.max(0, atMs) / 1000 : currentSeconds();
      const sample = { x: clamp(x, 0, view.cssWidth), y: clamp(y, 0, view.cssHeight), at };
      if (!state.pointer || at - state.pointer.at > 0.25) {
        state.pointer = state.lastWake = sample;
        state.velocity = 0;
        return false;
      }
      const dt = Math.max(1 / 240, at - state.pointer.at);
      const distance = Math.hypot(sample.x - state.pointer.x, sample.y - state.pointer.y);
      state.velocity += (distance / dt - state.velocity) * Math.min(1, dt / 0.08);
      state.pointer = sample;
      const span = Math.hypot(sample.x - state.lastWake.x, sample.y - state.lastWake.y);
      if (span < 18 || at - state.lastWake.at < 0.035) return false;
      const touch = settings().touch;
      const force = touch.stir * smooth((state.velocity - touch.slowPxS) / (touch.fastPxS - touch.slowPxS)) * span / (span + 90);
      state.lastWake = sample;
      if (force <= 0.04) return false;
      state.wakes.push({ x: sample.x, y: sample.y, at, strength: force });
      state.wakes = state.wakes.slice(-48);
      draw(state.manual || state.reducedMotion);
      schedule();
      return true;
    }

    function renderAt(milliseconds) {
      stopFrame();
      state.manual = true;
      if (Number.isFinite(milliseconds)) state.timeMs = Math.max(0, Number(milliseconds));
      draw(true);
      setOpacity(true);
      return stats();
    }

    function resumeLive() {
      state.manual = false;
      state.introStartMs = state.timeMs - 1000; // no second intro after a preview snapshot
      setOpacity(false);
      schedule();
    }

    function setRunning(value) {
      state.requestedRunning = Boolean(value);
      if (!state.requestedRunning) {
        stopFrame();
        draw(state.manual || state.reducedMotion);
      } else schedule();
      setOpacity(false);
    }

    function setReducedMotion(value) {
      state.reducedMotion = Boolean(value);
      if (state.reducedMotion) {
        stopFrame();
        draw(true);
      } else schedule();
      setOpacity(true);
    }

    function setDocumentVisible(value) {
      state.documentVisible = Boolean(value);
      if (!state.documentVisible) stopFrame();
      else schedule();
      setOpacity(false);
    }

    function stats() {
      const config = settings();
      const card = config.composition.card;
      const quietCenter = {
        calmX: view?.mobile ? Math.min(card.calmX, (view?.cssWidth ?? 0) * .28) : card.calmX,
        calmY: card.calmY,
        centerY: card.centerY,
        left: card.left
      };
      return {
        width: view?.cssWidth ?? 0,
        height: view?.cssHeight ?? 0,
        cellCount: view ? view.columns * view.rows : 0,
        rippleCount: state.taps.length,
        time: state.timeMs,
        paused: !state.requestedRunning,
        reducedMotion: state.reducedMotion,
        manual: state.manual,
        renderCount: state.renderCount,
        running: state.running,
        backend: 'webgl2',
        visualStyle: config.refinement.visualStyle,
        quietCenter,
        refinement: {
          depth: { ...config.refinement.depth },
          rippleLimit: 6,
          rippleLifetimeMs: 3800
        },
        seed: initial.seed >>> 0,
        lastImpulse: diagnosticTap(state.lastImpulse),
        impulses: state.taps.map(diagnosticTap)
      };
    }

    function cleanup(removeCanvas = true) {
      stopFrame();
      resizeObserver?.disconnect();
      canvas.removeEventListener('webglcontextlost', handleContextLost);
      if (gl && !contextLost) {
        if (atlasTexture) gl.deleteTexture(atlasTexture);
        if (program) gl.deleteProgram(program);
      }
      if (removeCanvas) canvas.remove();
    }

    function handleContextLost(event) {
      event.preventDefault();
      if (destroyed || contextLost) return;
      contextLost = true;
      stopFrame();
      onContextLost?.(event);
    }

    canvas.addEventListener('webglcontextlost', handleContextLost, false);

    Promise.resolve().then(() => {
      if (destroyed) return;
      try {
        gl = canvas.getContext('webgl2', {
          alpha: true, antialias: false, premultipliedAlpha: true,
          preserveDrawingBuffer: true, powerPreference: 'low-power'
        });
        if (!gl) throw new Error('WebGL2 is unavailable.');
        const vs = compile(gl, gl.VERTEX_SHADER, vertex);
        const fs = compile(gl, gl.FRAGMENT_SHADER, fragment);
        program = gl.createProgram();
        gl.attachShader(program, vs); gl.attachShader(program, fs); gl.linkProgram(program);
        gl.deleteShader(vs); gl.deleteShader(fs);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program) || 'WebGL program link failed.');
        gl.useProgram(program);
        atlasTexture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, atlasTexture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        integer('atlas', 0);
        started = true;
        resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(element);
        resize();
        setOpacity(false);
        onReady?.();
        schedule();
      } catch (error) {
        cleanup(false);
        onError?.(error);
      }
    });

    return {
      canvas,
      renderAt,
      impulse,
      wakeInput,
      stats,
      resumeLive,
      setRunning,
      setReducedMotion,
      setDocumentVisible,
      refresh: () => { resize(); draw(true); },
      destroy: () => { if (!destroyed) { destroyed = true; cleanup(); } }
    };
  }

export const GlyphWind: any = { defaults, pattern, grid, chooseLook, create };
